<script>
/**
 * O painel da conversa como CARTÃO FLUTUANTE no canto superior direito do
 * trilho (§5.1; kill-switch `PAINEL_PROGRESSO_CONTEXTO_ATIVO` — o
 * `messages.vue` o monta na casca sticky do índice de entregas). Referência:
 * o cartão "Progress / Outputs / Sources" do ChatGPT — fora do aside, o chat
 * segue com a largura toda.
 *
 * Seções (a vazia some): PROGRESSO (só com plano: anel + etapa atual, chevron
 * que abre a lista `PainelSecaoProgresso`), ENTREGAS (linha compacta: tile +
 * título; a ativa no aside fica realçada) e CONTEXTO (`PainelSecaoContexto`).
 * No PÉ, depois de todas elas, o cartão de suporte
 * (`suporte-whatsapp-card.vue`, variante `compacto`) — a casa dele com a
 * conversa já em andamento; no estado vazio ele mora abaixo do composer.
 *
 * A entrega abre no ASIDE: emite `abrir` (`{identidade, parteReal}`) ou, na
 * linha já ativa, `fechar` — os MESMOS eventos do `chat-indice-artefatos.vue`,
 * que o `messages.vue` já despacha (`abrirArtefatoDoIndice`).
 *
 * Visões (aberto / recolhido / sobreposto / oculto — este com o aside aberto
 * sem faixa nem para o botão) e a abertura automática em turno com plano:
 * `painel-flutuante-estado-mixin.js`. A lista de etapas mostra no máximo
 * `MAXIMO_DE_ETAPAS_VISIVEIS` (janela a partir da etapa atual).
 */
import { PAINEL_DO_CHAT } from '@modules/aawz-intelligence-v2/constants/TextChat'
import { resumoDoProgresso } from '@modules/aawz-intelligence-v2/utils/progresso-do-turno'
import SuporteWhatsappCard from '@modules/aawz-intelligence-v2/components/suporte-whatsapp/suporte-whatsapp-card.vue'
import dadosDoPainelMixin from './dados-do-painel-mixin'
import estadoMixin from './painel-flutuante-estado-mixin'
import progressoEncenadoMixin from './progresso-encenado-mixin'
import PainelAnelProgresso from './painel-anel-progresso.vue'
import PainelSecaoContexto from './painel-secao-contexto.vue'
import PainelSecaoProgresso from './painel-secao-progresso.vue'
import {
  ICONE_DAS_ENTREGAS,
  ICONE_DE_AVANCAR,
  ICONE_DE_RECOLHER,
  ALTURA_DA_LINHA_DE_ENTREGA,
  LARGURA_MAXIMA_DO_CARTAO,
  MAXIMO_DE_ETAPAS_VISIVEIS,
  MAX_ENTREGAS_VISIVEIS,
  TAMANHO_DO_ICONE_DA_ENTREGA,
  TAMANHO_DO_ICONE_DO_CONTROLE,
  TAMANHO_DO_ICONE_DO_PAINEL,
  larguraDoCartao,
} from './painel-do-chat.constantes'

export default {
  name: 'IntelligenceV2PainelFlutuante',
  components: {
    PainelAnelProgresso,
    PainelSecaoContexto,
    PainelSecaoProgresso,
    SuporteWhatsappCard,
  },
  mixins: [dadosDoPainelMixin, estadoMixin, progressoEncenadoMixin],
  props: {
    // Faixa livre ao lado do trilho (medida pelo `messages.vue`).
    larguraMaxima: {
      type: Number,
      default: null,
    },
  },
  data() {
    return {
      copy: PAINEL_DO_CHAT,
      ICONE_DAS_ENTREGAS,
      ICONE_DE_AVANCAR,
      ICONE_DE_RECOLHER,
      MAXIMO_DE_ETAPAS_VISIVEIS,
      TAMANHO_DO_ICONE_DA_ENTREGA,
      TAMANHO_DO_ICONE_DO_CONTROLE,
      TAMANHO_DO_ICONE_DO_PAINEL,
      // Hover por JS: a animação por ícone da navbar pede `is-animating`.
      entregaEmHover: null,
      // Teto medido da lista de entregas (3 linhas inteiras) — ver o computed.
      alturaDeTresEntregas: 0,
    }
  },
  computed: {
    // O anel e a etapa atual leem o MESMO que a lista mostra (encenado), senão
    // a fração já estaria cheia com a lista ainda andando.
    resumoExibido() {
      return resumoDoProgresso(this.progressoExibido)
    },
    temPlano() {
      return this.resumoDoPainel.temPlano
    },
    // Recolhido: o anel só enquanto o plano anda; terminado, o check não diz nada
    // útil — volta o ícone das entregas com a contagem.
    anelNoRecolhido() {
      return this.temPlano && !this.resumoExibido.encerrado
    },
    propsDoAnel() {
      const resumo = this.resumoExibido
      return {
        // ⚠️ MOVIMENTO só enquanto o TURNO trabalha — `resumo.emAndamento`
        // sozinho quer dizer "existe etapa corrente", que é outra coisa.
        // Achado do usuário na tela (18/09/2026): na etapa "Conhecer seu
        // escritório" o anel girava enquanto o servidor não fazia nada — a
        // etapa espera uma RESPOSTA DELE, no tempo dele. Spinner ali afirma
        // trabalho que não existe, e é o tipo de sinal que ensina a pessoa a
        // ignorar o indicador.
        emAndamento: resumo.emAndamento && this.turnoEmVoo,
        encerrado: resumo.encerrado,
        concluidas: resumo.concluidas,
        total: resumo.total,
      }
    },
    totalDeEntregas() {
      return this.entregasDoPainel.length
    },
    // Enquanto as etapas ainda estão aparecendo, a lista fica como estava no
    // começo da encenação: entrega que chega antes das etapas dela contaria a
    // história fora de ordem. A contagem do título segue sendo a total.
    entregasVisiveis() {
      if (!this.encenandoProgresso) return this.entregasDoPainel
      const antes = this.entregasNoInicioDaEncenacao
      return this.entregasDoPainel.filter((linha) => antes.indexOf(linha.identidade) !== -1)
    },
    rotuloDoProgresso() {
      return this.progressoAberto ? this.copy.OCULTAR_ETAPAS : this.copy.MOSTRAR_ETAPAS
    },
    estiloDoCartao() {
      const largura = (this.sobreposto ? LARGURA_MAXIMA_DO_CARTAO : larguraDoCartao(this.larguraMaxima)) + 'px'
      return { width: largura, minWidth: largura }
    },
    // O teto é MEDIDO na própria lista (3 linhas inteiras): a altura da linha
    // varia com o subtítulo e com a fonte do sistema, e a constante deixava
    // 40% de uma quarta linha aparecendo (achado do usuário, 17/09). A
    // constante segue como piso, para o primeiro quadro antes da medição.
    estiloDaListaDeEntregas() {
      const medida = this.alturaDeTresEntregas
      const teto = medida || MAX_ENTREGAS_VISIVEIS * ALTURA_DA_LINHA_DE_ENTREGA
      return { maxHeight: teto + 'px' }
    },
    linhasDeEntrega() {
      const emHover = this.entregaEmHover
      return this.entregasVisiveis.map(function (linha) {
        const destacada = linha.ativa || linha.identidade === emHover
        return {
          linha,
          ariaCurrent: linha.ativa ? 'true' : null,
          // O subtítulo só aparece quando conta a história da entrega: a
          // leitura em curso (ou o desfecho dela).
          subtituloVisivel: Boolean(linha.subtitulo),
          corDoIcone: destacada ? 'primary' : 'var(--neutral-600)',
          classesDoIcone: {
            'is-detailed': linha.iconeDetalhado,
            'is-animating': linha.identidade === emHover,
          },
        }
      })
    },
  },
  updated() {
    this.medirAlturaDasEntregas()
  },
  methods: {
    // Soma das 3 primeiras linhas + os gaps entre elas, lida do DOM.
    medirAlturaDasEntregas() {
      const lista = this.$refs.listaDeEntregas
      if (!lista || typeof lista.getBoundingClientRect !== 'function') return
      const linhas = lista.children ? Array.prototype.slice.call(lista.children, 0, MAX_ENTREGAS_VISIVEIS) : []
      if (linhas.length < MAX_ENTREGAS_VISIVEIS) {
        this.alturaDeTresEntregas = 0
        return
      }
      const topo = linhas[0].getBoundingClientRect().top
      const base = linhas[linhas.length - 1].getBoundingClientRect().bottom
      const medida = Math.ceil(base - topo)
      if (medida > 0 && medida !== this.alturaDeTresEntregas) this.alturaDeTresEntregas = medida
    },
    escolherEntrega(linha) {
      if (linha.ativa) {
        this.$emit('fechar')
      } else {
        this.$emit('abrir', { identidade: linha.identidade, parteReal: linha.parteReal })
      }
      this.abertoPorCima = false
    },
  },
}
</script>

<template>
  <div class="painel-flutuante d-flex justify-end" data-test="painel-flutuante">
    <!-- Sem `mode`: botão e cartão coexistem na troca (quem sai vira absoluto
         no canto) — nunca há um quadro sem nada no canto. -->
    <transition name="painel-flutuante-troca">
      <button
        v-if="recolhido"
        key="recolhido"
        type="button"
        class="painel-flutuante__recolhido d-inline-flex align-center justify-center"
        aria-expanded="false"
        :aria-label="copy.EXPANDIR"
        :title="copy.EXPANDIR"
        data-test="painel-flutuante-expandir"
        @click="expandir"
      >
        <PainelAnelProgresso v-if="anelNoRecolhido" v-bind="propsDoAnel" />
        <icon-svg v-else :name="ICONE_DAS_ENTREGAS" :size="TAMANHO_DO_ICONE_DO_PAINEL" color="var(--neutral-700)" />
        <span v-if="totalDeEntregas" class="painel-flutuante__recolhido-contagem ia-texto-micro" data-test="painel-flutuante-recolhido-contagem">{{
          totalDeEntregas
        }}</span>
      </button>

      <nav
        v-else-if="!oculto"
        key="cartao"
        class="painel-flutuante__cartao"
        :class="{ 'painel-flutuante__cartao--sobreposto': sobreposto }"
        :style="estiloDoCartao"
        :aria-label="copy.ARIA"
        data-test="painel-flutuante-cartao"
      >
        <button
          type="button"
          class="painel-flutuante__recolher d-inline-flex align-center justify-center"
          aria-expanded="true"
          :aria-label="copy.RECOLHER"
          :title="copy.RECOLHER"
          data-test="painel-flutuante-recolher"
          @click="recolher"
        >
          <icon-svg :name="ICONE_DE_RECOLHER" :size="TAMANHO_DO_ICONE_DO_CONTROLE" color="var(--neutral-600)" />
        </button>

        <div class="painel-flutuante__corpo d-flex flex-column az-gap pa-3">
          <section v-if="temPlano" class="d-flex flex-column az-gap-small" data-test="painel-flutuante-progresso">
            <button
              type="button"
              class="painel-flutuante__progresso d-flex align-center az-gap-small text-left"
              :aria-expanded="progressoAberto ? 'true' : 'false'"
              :aria-label="rotuloDoProgresso"
              data-test="painel-flutuante-progresso-alternar"
              @click="alternarProgresso"
            >
              <PainelAnelProgresso v-bind="propsDoAnel" />
              <span class="painel-flutuante__texto d-flex flex-column">
                <span class="d-inline-flex align-center az-gap-xxsmall az-text-primary az-size-14 font-weight-medium">
                  {{ copy.PROGRESSO }}
                  <icon-svg
                    class="painel-flutuante__chevron"
                    :class="{ 'painel-flutuante__chevron--aberto': progressoAberto }"
                    :name="ICONE_DE_AVANCAR"
                    :size="TAMANHO_DO_ICONE_DO_CONTROLE"
                    color="var(--neutral-600)"
                  />
                </span>
                <span class="ia-sidebar-aux text-truncate" data-test="painel-flutuante-progresso-resumo">{{
                  resumoExibido.titulo
                }}</span>
              </span>
            </button>
            <v-expand-transition>
              <div v-show="progressoAberto" data-test="painel-flutuante-etapas">
                <PainelSecaoProgresso
                  sem-titulo
                  :passos="progressoExibido"
                  compacto
                  :turno-parado="!turnoEmVoo"
                  :max-visiveis="MAXIMO_DE_ETAPAS_VISIVEIS"
                />
              </div>
            </v-expand-transition>
          </section>

          <section
            v-if="linhasDeEntrega.length"
            class="painel-flutuante__entregas d-flex flex-column az-gap-xxsmall"
            data-test="painel-flutuante-entregas"
          >
            <h3 class="az-text-primary az-size-14 font-weight-medium d-flex align-center az-gap-small ma-0 mb-1">
              {{ copy.ENTREGAS }}
              <span class="ia-sidebar-contador" data-test="painel-flutuante-entregas-contagem">{{ totalDeEntregas }}</span>
            </h3>
            <ul
              ref="listaDeEntregas"
              class="painel-flutuante__lista d-flex flex-column"
              :style="estiloDaListaDeEntregas"
              data-test="painel-flutuante-lista-de-entregas"
            >
              <li v-for="item in linhasDeEntrega" :key="item.linha.identidade">
                <button
                  type="button"
                  class="painel-flutuante__entrega d-flex align-center az-gap-small text-left"
                  :title="item.linha.titulo"
                  :aria-current="item.ariaCurrent"
                  data-test="painel-flutuante-entrega"
                  @click="escolherEntrega(item.linha)"
                  @mouseenter="entregaEmHover = item.linha.identidade"
                  @mouseleave="entregaEmHover = null"
                >
                  <span class="painel-flutuante__tile d-inline-flex align-center justify-center flex-shrink-0">
                    <v-progress-circular
                      v-if="item.linha.emAndamento"
                      indeterminate
                      :size="TAMANHO_DO_ICONE_DA_ENTREGA"
                      :width="1.5"
                      color="primary"
                      data-test="painel-flutuante-entrega-em-andamento"
                    />
                    <icon-svg
                      v-else
                      class="navbar-main-icon"
                      :class="item.classesDoIcone"
                      :name="item.linha.icone"
                      :size="TAMANHO_DO_ICONE_DA_ENTREGA"
                      :color="item.corDoIcone"
                    />
                  </span>
                  <span class="painel-flutuante__texto-da-entrega d-flex flex-column">
                    <span class="painel-flutuante__titulo-da-entrega az-text-primary az-size-12 text-truncate">{{
                      item.linha.titulo
                    }}</span>
                    <span
                      v-if="item.subtituloVisivel"
                      class="ia-sidebar-aux text-truncate"
                      data-test="painel-flutuante-entrega-subtitulo"
                    >{{ item.linha.subtitulo }}</span>
                  </span>
                </button>
              </li>
            </ul>
          </section>

          <PainelSecaoContexto v-if="contextoDoPainel" :contexto="contextoDoPainel" />

          <!-- Suporte no PÉ do painel, depois de tudo: com a conversa em
               andamento o estado vazio (e o cartão que mora embaixo do
               composer lá) não existe mais, e esta é a única casa que resta
               para o mesmo destino. Variante `compacto` porque a coluna aqui
               tem 200-240px. -->
          <SuporteWhatsappCard compacto />
        </div>
      </nav>
    </transition>
  </div>
</template>

<style lang="sass" scoped>
// Mesma animação por ícone da navbar e do card de entrega — reuso do partial.
@import '@modules/navegacao-v2/styles/_nav-icon-tells.sass'

$respiro-do-controle: 8px
$lado-do-controle: 20px

// Casca comum do botão recolhido e do cartão: branco, hairline, sombra suave,
// raio grande (a mesma do índice de entregas).
.painel-flutuante__recolhido,
.painel-flutuante__cartao
  box-sizing: border-box
  border: 1px solid var(--border-hairline)
  border-radius: var(--radius-lg)
  background: var(--white)
  box-shadow: var(--shadow-sm)

.painel-flutuante__recolhido
  // Quadrado (36×36, raio médio); a contagem vira selo no canto.
  position: relative
  border-radius: var(--radius-md)
  width: 36px
  height: 36px
  padding: 0
  cursor: pointer
  transition: background var(--motion-duration-fast) var(--motion-easing-standard)

  &:hover
    background: var(--sidebar-warm-hover)

  &:focus-visible
    outline: 2px solid var(--primary)
    outline-offset: 2px

.painel-flutuante__recolhido-contagem
  position: absolute
  top: -6px
  right: -6px
  min-width: 16px
  height: 16px
  padding: 0 4px
  border-radius: var(--radius-sm)
  background: var(--primary)
  color: var(--white)
  // O passo de 10px vem de `design/_ia-texto.sass` (classe no template).
  line-height: 16px
  text-align: center

.painel-flutuante__cartao
  position: relative
  overflow: hidden

// Aberto POR CIMA do chat (faixa estreita): sombra de camada.
.painel-flutuante__cartao--sobreposto
  box-shadow: var(--shadow-pop)

.painel-flutuante__recolher
  position: absolute
  top: $respiro-do-controle
  right: $respiro-do-controle
  width: $lado-do-controle
  height: $lado-do-controle
  border: 0
  border-radius: var(--radius-sm)
  background: transparent
  cursor: pointer
  z-index: 1

  &:hover,
  &:focus-visible
    background: var(--sidebar-warm-hover)
    outline: 0

// Teto do corpo (rola por dentro): o cartão aberto termina acima do índice de
// mensagens, centrado a 70% da área (`messages.vue`). Medido com 18 perguntas
// (coluna de 154px): a 1366×768 o índice começa em y=388 e o cartão termina em
// 381; a 1920×1080, 606 × 600. O respiro à direita da PRIMEIRA seção deixa o
// recolher sem cobrir o texto.
$teto-do-corpo: calc(70vh - 240px)

.painel-flutuante__corpo
  max-height: $teto-do-corpo
  overflow-y: auto

  > :first-child
    padding-right: $lado-do-controle

.painel-flutuante__progresso
  width: 100%
  border: 0
  padding: 0
  background: transparent
  cursor: pointer

  &:focus-visible
    border-radius: var(--radius-sm)
    outline: 2px solid var(--primary)
    outline-offset: 2px

.painel-flutuante__texto,
.painel-flutuante__texto-da-entrega,
.painel-flutuante__titulo-da-entrega
  min-width: 0
  line-height: 1.33

.painel-flutuante__chevron
  transition: transform var(--motion-duration-fast) var(--motion-easing-standard)

.painel-flutuante__chevron--aberto
  transform: rotate(90deg)

// No máximo 3 linhas (`estiloDaListaDeEntregas`); o resto rola com a barra fina
// da lateral. A lista sangra 6px e devolve em padding: o hover da linha continua
// com respiro sem ser cortado pelo `overflow`.
.painel-flutuante__lista
  list-style: none
  margin: 0 -6px
  padding: 0 6px
  overflow-x: hidden
  overflow-y: auto
  scrollbar-width: thin
  scrollbar-color: var(--sidebar-warm-border) transparent

  &::-webkit-scrollbar
    width: 4px

  &::-webkit-scrollbar-track
    background: transparent

  &::-webkit-scrollbar-thumb
    border-radius: var(--radius-sm)
    background: var(--sidebar-warm-border)

// A linha "sangra" 6px para os lados: o hover ganha respiro e o tile segue
// alinhado com o título da seção.
.painel-flutuante__entrega
  width: calc(100% + 12px)
  margin: 0 -6px
  padding: 4px 6px
  border: 0
  border-radius: var(--radius-sm)
  background: transparent
  cursor: pointer
  transition: background var(--motion-duration-fast) var(--motion-easing-standard)

  &:hover
    background: var(--neutral-100)

  &:focus-visible
    outline: 2px solid var(--primary)
    outline-offset: -2px

  &[aria-current="true"]
    background: color-mix(in srgb, var(--primary) 6%, var(--white))

    .painel-flutuante__titulo-da-entrega
      font-weight: 600

.painel-flutuante__tile
  width: 24px
  height: 24px
  border-radius: var(--radius-sm)
  background: var(--neutral-100)

.painel-flutuante__entrega:hover .painel-flutuante__tile,
.painel-flutuante__entrega[aria-current="true"] .painel-flutuante__tile
  background: color-mix(in srgb, var(--primary) 10%, var(--white))

// Troca botão ↔ cartão, ancorada no canto superior direito.
.painel-flutuante-troca-enter-active,
.painel-flutuante-troca-leave-active
  transition: opacity var(--motion-duration-default) var(--motion-easing-standard), transform var(--motion-duration-default) var(--motion-easing-standard)
  transform-origin: top right

.painel-flutuante-troca-leave-active
  position: absolute
  top: 0
  right: 0
  pointer-events: none

.painel-flutuante-troca-enter,
.painel-flutuante-troca-leave-to
  opacity: 0
  transform: scale(0.96)

@media (prefers-reduced-motion: reduce)
  .painel-flutuante__recolhido,
  .painel-flutuante__chevron,
  .painel-flutuante__entrega,
  .painel-flutuante-troca-enter-active,
  .painel-flutuante-troca-leave-active
    transition: none
</style>
